<template>
    <div class="scanner-status-widget" :class="{ 'scanner-widget-dark': theme === THEMES.DARK }">
        <div class="scanner-header">
            <div class="scanner-title">
                <i class="material-icons scanner-icon">scanner</i>
                <span>Wireless Scanner</span>
            </div>
            <div class="scanner-controls">
                <button 
                    class="btn btn-sm scanner-toggle-btn" 
                    :class="scannerActive ? 'btn-success' : 'btn-outline-primary'"
                    @click="toggleScanner"
                    :disabled="!scannerConnected"
                >
                    {{ scannerActive ? 'Stop' : 'Start' }}
                </button>
                <button 
                    class="btn btn-sm btn-outline-secondary"
                    @click="testScan"
                    :disabled="!scannerActive"
                    title="Test Scanner"
                >
                    <i class="material-icons">science</i>
                </button>
            </div>
        </div>
        
        <div class="scanner-status-row">
            <div class="status-indicator" :class="`status-${scannerStatusColor}`">
                <div class="status-dot"></div>
                <span class="status-text">{{ scannerStatus }}</span>
            </div>
            <div class="scanner-info" v-if="showDetails">
                <small class="text-muted">{{ printerIP }}</small>
            </div>
        </div>
        
        <div class="scanner-stats" v-if="showStats && scanHistory.length > 0">
            <div class="stat-item">
                <span class="stat-label">Last Scan:</span>
                <code class="stat-value">{{ lastScanData || 'None' }}</code>
            </div>
            <div class="stat-item">
                <span class="stat-label">Total Scans:</span>
                <span class="stat-value">{{ scanHistory.length }}</span>
            </div>
        </div>
        
        <div class="scanner-actions" v-if="showActions">
            <button 
                class="btn btn-sm btn-outline-info"
                @click="showScannerModal = true"
                title="Scanner Settings"
            >
                <i class="material-icons">settings</i>
            </button>
            <button 
                class="btn btn-sm btn-outline-warning"
                @click="clearScanHistory"
                :disabled="scanHistory.length === 0"
                title="Clear History"
            >
                <i class="material-icons">clear_all</i>
            </button>
        </div>
        
        <!-- Scanner Modal -->
        <div v-if="showScannerModal" class="modal fade show" style="display: block" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content" :class="{ 'dark': theme === THEMES.DARK }">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="material-icons mr-2">scanner</i>
                            Wireless Scanner Status
                        </h5>
                        <button type="button" class="close" @click="showScannerModal = false">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="scanner-details">
                            <div class="detail-row">
                                <strong>Status:</strong>
                                <span :class="`text-${scannerStatusColor}`">{{ scannerStatus }}</span>
                            </div>
                            <div class="detail-row">
                                <strong>Printer IP:</strong>
                                <code>{{ printerIP }}</code>
                            </div>
                            <div class="detail-row">
                                <strong>Connected:</strong>
                                <span :class="scannerConnected ? 'text-success' : 'text-danger'">
                                    {{ scannerConnected ? 'Yes' : 'No' }}
                                </span>
                            </div>
                            <div class="detail-row">
                                <strong>Active:</strong>
                                <span :class="scannerActive ? 'text-success' : 'text-warning'">
                                    {{ scannerActive ? 'Yes' : 'No' }}
                                </span>
                            </div>
                            <div class="detail-row">
                                <strong>Last Scan:</strong>
                                <code>{{ lastScanData || 'None' }}</code>
                            </div>
                            <div class="detail-row">
                                <strong>Total Scans:</strong>
                                <span>{{ scanHistory.length }}</span>
                            </div>
                        </div>
                        
                        <div class="scanner-history" v-if="scanHistory.length > 0">
                            <h6 class="mt-3">Recent Scans</h6>
                            <div class="history-list">
                                <div 
                                    v-for="(scan, index) in scanHistory.slice(0, 10)" 
                                    :key="index"
                                    class="history-item"
                                >
                                    <code>{{ scan }}</code>
                                </div>
                            </div>
                        </div>
                        
                        <div class="scanner-test mt-3">
                            <h6>Test Scanner</h6>
                            <div class="input-group">
                                <input 
                                    v-model="testScanValue"
                                    type="text" 
                                    class="form-control" 
                                    placeholder="Enter test value"
                                >
                                <div class="input-group-append">
                                    <button 
                                        class="btn btn-outline-primary" 
                                        type="button"
                                        @click="runTestScan"
                                        :disabled="!testScanValue.trim()"
                                    >
                                        Test
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button 
                            type="button" 
                            class="btn btn-outline-warning"
                            @click="clearScanHistory"
                            :disabled="scanHistory.length === 0"
                        >
                            Clear History
                        </button>
                        <button 
                            type="button" 
                            class="btn btn-outline-info"
                            @click="reconnectScanner"
                        >
                            Reconnect
                        </button>
                        <button type="button" class="btn btn-secondary" @click="showScannerModal = false">
                            Close
                        </button>
                    </div>
                </div>
            </div>
            <div class="modal-backdrop fade show"></div>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex"
import ScannerMixin from "@/mixins/scanner-mixin"
import wirelessScannerService from "@/services/wireless-scanner-service"

export default {
    name: "ScannerStatus",
    mixins: [ScannerMixin],
    props: {
        showDetails: {
            type: Boolean,
            default: true
        },
        showStats: {
            type: Boolean,
            default: true
        },
        showActions: {
            type: Boolean,
            default: true
        },
        compact: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showScannerModal: false,
            testScanValue: '',
            printerIP: '10.0.0.201'
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES"])
    },
    methods: {
        async runTestScan() {
            if (this.testScanValue.trim()) {
                this.testScan(this.testScanValue.trim());
                this.testScanValue = '';
                this.$toast.success('Test scan sent!');
            }
        },
        
        async reconnectScanner() {
            try {
                this.$toast.info('Reconnecting scanner...');
                await wirelessScannerService.reconnect();
                this.$toast.success('Scanner reconnected successfully!');
            } catch (error) {
                console.error('Failed to reconnect scanner:', error);
                this.$toast.error('Failed to reconnect scanner');
            }
        },
        
        onScanReceived(scanData) {
            // This component doesn't handle scans directly, 
            // but we can show a toast notification
            this.$toast.success(`Scan received: ${scanData}`);
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.scanner-status-widget {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    font-size: 14px;
    
    &.scanner-widget-dark {
        background: $primaryColorDark;
        border-color: $darkBorderColor;
        color: $darkTextColor;
    }
}

.scanner-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.scanner-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    
    .scanner-icon {
        margin-right: 8px;
        font-size: 18px;
        color: $primaryColor;
    }
}

.scanner-controls {
    display: flex;
    gap: 6px;
    
    .btn {
        padding: 4px 8px;
        font-size: 12px;
        
        .material-icons {
            font-size: 16px;
        }
    }
}

.scanner-status-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

    .status-indicator {
    display: flex;
    align-items: center;
    
    .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
    }
    
    &.status-green .status-dot {
        background-color: $successColor;
    }
    
    &.status-orange .status-dot {
        background-color: $warningColor;
    }
    
    &.status-red .status-dot {
        background-color: $dangerColor;
    }
    
    .status-text {
        font-size: 12px;
        font-weight: 500;
    }
}

.scanner-info {
    text-align: right;
    
    small {
        font-size: 10px;
    }
}

.scanner-stats {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
    
    .stat-item {
        display: flex;
        flex-direction: column;
        
        .stat-label {
            font-size: 10px;
            color: rgba($textColor, 0.7);
            margin-bottom: 2px;
        }
        
        .stat-value {
            font-size: 12px;
            font-weight: 500;
        }
    }
}

/* Dark overrides */
.dark .status-indicator {
    &.status-green .status-dot { background-color: $successColorDark; }
    &.status-orange .status-dot { background-color: $warningColorDark; }
    &.status-red .status-dot { background-color: $dangerColorDark; }
}
.dark .scanner-stats .stat-item .stat-label { color: rgba($darkTextColor, 0.7); }

.scanner-actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
    
    .btn {
        padding: 4px 8px;
        font-size: 12px;
        
        .material-icons {
            font-size: 16px;
        }
    }
}

.scanner-details {
    .detail-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 0;
        border-bottom: 1px solid $lightBorderColor;
        
        &:last-child {
            border-bottom: none;
        }
    }
}

.scanner-history {
    .history-list {
        max-height: 200px;
        overflow-y: auto;
        border: 1px solid $lightBorderColor;
        border-radius: 4px;
        padding: 8px;
        
        .history-item {
            padding: 4px 0;
            border-bottom: 1px solid $lightBorderColor;
            
            &:last-child {
                border-bottom: none;
            }
            
            code {
                background: $lightHoverColor;
                padding: 2px 4px;
                border-radius: 3px;
                font-size: 12px;
            }
        }
    }
}

/* Dark theme adjustments for history and details */
.dark .scanner-status-widget {
    background: $darkCardBg;
    border-color: $darkBorderColor;
}
.dark .scanner-details .detail-row { border-bottom-color: $darkBorderColor; }
.dark .scanner-history .history-list { border-color: $darkBorderColor; }
.dark .scanner-history .history-item { border-bottom-color: $darkBorderColor; }
.dark .scanner-history .history-item code { background: lighten($darkCardBg, 6%); }

.scanner-test {
    .input-group {
        input {
            font-size: 14px;
        }
        
        .btn {
            font-size: 14px;
        }
    }
}

// Responsive design
@media (max-width: 768px) {
    .scanner-status-widget {
        font-size: 12px;
        
        .scanner-title {
            font-size: 14px;
        }
        
        .scanner-controls .btn {
            padding: 3px 6px;
            font-size: 11px;
        }
    }
}
</style> 
